import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import DualActionFooter from '@/components/feature/DualActionFooter';
import HowWeVetEngineers from '@/components/feature/HowWeVetEngineers';

const generateServiceSchema = () => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Offshore Development Team India",
  "provider": { "@type": "Organization", "name": "Thoughtic Solutions", "url": "https://thoughtic.com" },
  "description": "Scale your engineering capacity with a dedicated offshore development team in India. 300+ pre-vetted engineers, 60% cost savings vs US rates, 8-hour timezone overlap.",
  "serviceType": "Offshore Development Team",
  "url": "https://thoughtic.com/offshore-team-india",
  "areaServed": [
    { "@type": "Country", "name": "United States" },
    { "@type": "Country", "name": "United Kingdom" },
    { "@type": "Country", "name": "Germany" },
    { "@type": "Country", "name": "Australia" }
  ]
});

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

/* ── Hero ── */
function Hero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-16 md:pt-40 md:pb-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20corporate%20background%20with%20world%20map%20and%20connection%20lines%20between%20India%20and%20global%20cities%2C%20glowing%20network%20nodes%2C%20deep%20navy%20blue%20with%20saffron%20orange%20and%20emerald%20green%20accents%20representing%20India%2C%20professional%20global%20outsourcing%20theme%2C%20soft%20bokeh%20lighting%2C%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20modern%20business%20technology%20aesthetic&width=1600&height=900&seq=india-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-global-line text-amber-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">India-Based Teams</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Offshore Development
            <br />
            <span className="text-primary-400">Team India</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-2xl mx-auto"
          >
            Scale your engineering capacity with a dedicated offshore development team in India. World-class talent, significant cost savings, and seamless timezone overlap.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Build Your India Team
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/how-it-works"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              How Engagement Works
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

/* ── Stats Bar ── */
function StatsBar() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const stats = [
    { value: '300+', label: 'India Engineers', icon: 'ri-user-search-line' },
    { value: '60%', label: 'Cost Savings', icon: 'ri-money-rupee-circle-line' },
    { value: '8h', label: 'US Time Overlap', icon: 'ri-time-line' },
    { value: '100%', label: 'English Fluent', icon: 'ri-translate-2' },
  ];

  return (
    <section className="py-10 md:py-14 bg-white border-b border-gray-100" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + i * 0.08, ...springTransition }}
              className="text-center"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 mx-auto mb-2">
                <i className={`${stat.icon} text-amber-600 text-lg`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-0.5">{stat.value}</div>
              <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Why India ── */
function WhyIndia() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const reasons = [
    {
      icon: 'ri-money-rupee-circle-line',
      title: 'Significant Cost Efficiency',
      desc: 'Hire senior engineers in India at 40-60% lower cost than US or European equivalents — without compromising on code quality, communication, or delivery speed.',
    },
    {
      icon: 'ri-time-line',
      title: 'Optimal Timezone Overlap',
      desc: 'India-based teams provide 4-8 hours of real-time overlap with US and European business hours. Morning standups, afternoon reviews, and end-of-day handoffs just work.',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Elite Engineering Pipeline',
      desc: 'India produces 1.5 million engineering graduates annually. We tap the top percentile — engineers from IITs, NITs, and top product companies with proven production experience.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Enterprise-Grade Security',
      desc: 'NDAs, IP assignment, SOC 2 compliant infrastructure, and isolated development environments. Your code and data never leave your control.',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-global-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why India for Offshore Development</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            The India Advantage for
            <span className="text-primary-600"> Global Teams</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            India remains the world's premier offshore development destination for a reason. Here is why 200+ companies choose Thoughtic for their India-based engineering teams.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {reasons.map((r, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${r.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{r.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Engagement Models ── */
function EngagementModels() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const models = [
    {
      title: 'Dedicated Development Team',
      desc: 'A fully managed autonomous squad of 3-10 engineers, a Tech Lead, and a QA engineer — all based in India and reporting to you daily.',
      badge: 'Most Popular',
      icon: 'ri-team-line',
    },
    {
      title: 'Staff Augmentation',
      desc: 'Individual engineers who join your existing team and follow your sprints, standups, and tooling. Scale up or down with 2 weeks notice.',
      badge: 'Flexible',
      icon: 'ri-user-add-line',
    },
    {
      title: 'Offshore Development Center',
      desc: 'A long-term scalable delivery hub in India with dedicated office space, infrastructure, and ongoing HR and operational support.',
      badge: 'Enterprise',
      icon: 'ri-building-2-line',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-amber-100/40 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-primary-100/40 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-briefcase-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Engagement Models</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Build Your India Team
            <span className="text-primary-600"> Your Way</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Whether you need one engineer or a full offshore delivery center, we have a model that fits your scale, budget, and governance needs.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {models.map((m, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition }}
              className="group bg-white rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl group-hover:scale-110 transition-transform duration-300">
                  <i className={`${m.icon} text-xl text-white`}></i>
                </div>
                <span className="px-3 py-1 bg-amber-50 border border-amber-200 rounded-full text-xs font-semibold text-amber-700">
                  {m.badge}
                </span>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">{m.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{m.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── How It Works India ── */
function HowItWorksIndia() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const steps = [
    {
      num: '01',
      title: 'Define Your Needs',
      desc: 'Share your tech stack, team size, and project roadmap. We advise on the optimal India team structure and skill mix.',
    },
    {
      num: '02',
      title: 'We Source & Vet',
      desc: 'Our India recruitment team taps into 300+ pre-vetted engineers and runs targeted searches for specialized roles.',
    },
    {
      num: '03',
      title: 'You Interview & Select',
      desc: 'Meet shortlisted candidates over video. Conduct technical interviews or pair-programming sessions until you are confident.',
    },
    {
      num: '04',
      title: 'Onboard & Scale',
      desc: 'Engineers start within 5-10 business days. We handle payroll, compliance, equipment, and workspace so you focus on shipping.',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 shadow-sm"
          >
            <i className="ri-route-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Our Process</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            From Brief to
            <span className="text-primary-600"> Deployed Team</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Launching an offshore development team in India does not have to take months. Here is how we get you operational in under two weeks.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.1, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="text-3xl font-black text-primary-200 mb-3">{step.num}</div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Trust Signals ── */
function TrustSignals() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const signals = [
    { icon: 'ri-shield-check-line', title: 'SOC 2 Type II', desc: 'Certified security and data protection practices.' },
    { icon: 'ri-file-shield-line', title: 'NDA & IP Protection', desc: 'All code ownership transfers to you on day one.' },
    { icon: 'ri-lock-line', title: 'Isolated Environments', desc: 'Dedicated VPNs, zero-trust access, and client-managed infra.' },
    { icon: 'ri-global-line', title: 'GDPR & HIPAA Ready', desc: 'Compliance frameworks built into every engagement.' },
  ];

  return (
    <section className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4">
            <i className="ri-shield-check-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Enterprise Trust</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4">
            Security & Compliance
            <span className="text-primary-400"> Built In</span>
          </h2>
          <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto">
            Offshore does not mean off-guard. Every India-based engagement is protected by the same enterprise-grade safeguards we offer domestic clients.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {signals.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 + i * 0.08, ...springTransition }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 text-center"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mx-auto mb-4">
                <i className={`${s.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-base font-bold text-white mb-1">{s.title}</h3>
              <p className="text-sm text-white/60">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Final CTA ── */
function FinalCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 right-0 w-[400px] h-[400px] bg-amber-100/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 left-0 w-[400px] h-[400px] bg-primary-100/30 rounded-full blur-3xl"></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-6 shadow-sm">
            <i className="ri-global-line text-amber-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Start Building in India</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6">
            Launch Your Offshore
            <span className="text-primary-600"> Team This Month</span>
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 md:mb-10">
            From a single engineer to a full offshore development center — we build India-based teams that ship quality code, communicate clearly, and scale on demand.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/talent"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Build Your India Team
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/pricing"
              className="px-8 py-4 border-2 border-navy-900/20 text-navy-900 text-base font-semibold rounded-xl hover:bg-navy-900 hover:text-white hover:border-navy-900 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              View India Pricing
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function OffshoreTeamIndiaPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateServiceSchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout
      title="Offshore Development Team India — 60% Cost Savings"
      description="Build a dedicated offshore development team in India. 300+ pre-vetted engineers, 60% cost savings vs US rates, 8-hour timezone overlap. Scale your engineering capacity today."
      keywords="offshore development team India, hire developers in India, India software engineers, remote team India, offshore IT team, dedicated development center India, Indian engineers for hire, cost effective software development"
    >
      <Hero />
      <StatsBar />
      <WhyIndia />
      <EngagementModels />
      <HowItWorksIndia />
      <TrustSignals />
      <HowWeVetEngineers
        accentColor="amber"
        heading="How We Vet India-Based Engineers"
        specialtyNote="For our India engineering network, we run the same 6-stage screening as our global pool — with additional English fluency assessment, timezone overlap validation, and remote-work discipline testing."
      />
      <FinalCTA />
      <DualActionFooter />
    </PageLayout>
  );
}